import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Plus, Check, X, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const TYPES = { decouverte: "Découverte", coordination: "Coordination mariage", location: "Location & décoration", depot: "Dépôt / retrait", telephone_visio: "Téléphone / visio", partenaire: "Partenaire" };
const STATUS_LABELS = { demande: "Nouvelle demande", planifie: "En attente", confirme: "Confirmée", annule: "Annulée", effectue: "Passée" };
const STATUS_TEXT = { demande: "Demandé", planifie: "Planifié", confirme: "Confirmé", annule: "Annulé", effectue: "Effectué" };
const SLOTS = ["9 h – 11 h", "11 h – 13 h", "14 h – 16 h", "16 h – 18 h"];

const EMPTY = { customer_name: "", customer_email: "", type: "decouverte", date: "", slot: SLOTS[0], notes: "" };

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/appointments").then((r) => setAppointments(r.data)).catch(() => setAppointments([]));
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    (appointments || []).forEach((a) => { (map[a.date] = map[a.date] || []).push(a); });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [appointments]);

  const submit = async () => {
    try {
      await api.post("/admin/appointments", form);
      toast.success("Rendez-vous planifié dans l'agenda Art Event");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status });
      toast.success(`Rendez-vous ${STATUS_TEXT[status].toLowerCase()} — notification créée`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!appointments) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="appointments-loading">Chargement…</div>;

  return (
    <div data-testid="admin-appointments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B13 — Rendez-vous &amp; Agenda</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Agenda Art Event</h1>
          <p className="mt-1 text-sm text-muted-foreground">Agenda exclusif Art Event — créneaux du lundi au samedi, 9 h à 18 h.</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="appointment-create">
          <Plus className="h-4 w-4" /> Nouveau rendez-vous
        </button>
      </div>

      {grouped.length === 0 ? (
        <div className="mt-6"><EmptyState icon={CalendarDays} title="Aucun rendez-vous">Les demandes publiques et les rendez-vous planifiés apparaîtront ici.</EmptyState></div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="appointments-list">
          {grouped.map(([date, appts]) => (
            <div key={date} className="card-ae">
              <p className="border-b border-border bg-cream/70 px-5 py-3 font-heading text-base font-semibold text-deepgreen">{date}</p>
              <ul className="divide-y divide-border">
                {appts.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`appointment-${a.id}`}>
                    <span className="w-28 rounded-lg bg-sage-light px-3 py-1.5 text-center text-xs font-bold text-deepgreen">{a.slot}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-deepgreen">{TYPES[a.type] || a.type} — {a.customer_name || a.customer_email || "—"}</p>
                      {a.notes && <p className="mt-0.5 text-xs text-muted-foreground">{a.notes}</p>}
                    </div>
                    <StatusBadge status={STATUS_LABELS[a.status] || a.status} />
                    <div className="flex gap-2">
                      {["demande", "planifie"].includes(a.status) && (
                        <button type="button" onClick={() => setStatus(a, "confirme")} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`appointment-confirm-${a.id}`}>
                          <Check className="h-3.5 w-3.5" /> Confirmer
                        </button>
                      )}
                      {a.status === "confirme" && (
                        <button type="button" onClick={() => setStatus(a, "effectue")} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`appointment-done-${a.id}`}>
                          <CheckCheck className="h-3.5 w-3.5" /> Effectué
                        </button>
                      )}
                      {!["annule", "effectue"].includes(a.status) && (
                        <button type="button" onClick={() => setStatus(a, "annule")} className="rounded-full border border-red-200 px-3 py-1.5 text-[11px] font-bold text-red-600 hover:bg-red-50" data-testid={`appointment-cancel-${a.id}`}>
                          <X className="h-3.5 w-3.5" /> Annuler
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="appointment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">Nouveau rendez-vous</DialogTitle>
            <DialogDescription>Agenda Art Event — rattachable à un client et un événement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="ap-name">Nom du contact</label><input id="ap-name" className="input-ae" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="appointment-name" /></div>
              <div><label className="label-ae" htmlFor="ap-email">E-mail</label><input id="ap-email" type="email" className="input-ae" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="appointment-email" /></div>
              <div><label className="label-ae" htmlFor="ap-type">Type</label>
                <select id="ap-type" className="input-ae" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="appointment-type">
                  {Object.entries(TYPES).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </div>
              <div><label className="label-ae" htmlFor="ap-slot">Créneau</label>
                <select id="ap-slot" className="input-ae" value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value })} data-testid="appointment-slot">
                  {SLOTS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="label-ae" htmlFor="ap-date">Date</label><input id="ap-date" type="date" className="input-ae" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appointment-date" /></div>
            </div>
            <div><label className="label-ae" htmlFor="ap-notes">Notes internes</label><input id="ap-notes" className="input-ae" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appointment-notes" /></div>
            <button type="button" onClick={submit} className="btn-primary-ae modal-sticky-btn w-full" data-testid="appointment-submit">Planifier</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
